/**
 * SandboxToolbarButton — the Flow Designer toolbar control for the sandbox
 * environment backing the flow's run target. Lives next to the Settings gear
 * (see FlowDesignerCanvas) and is the single affordance for warm / stop / open.
 *
 * State (from useSandboxEnvironment / SandboxEnvironmentState) drives the icon,
 * colour, tooltip, and press behaviour:
 *   - asleep   → click to warm (ensure the backing instance)
 *   - warming  → spinner; click opens the provisioning checklist + live timer
 *   - ready    → click opens a menu: Open ↗ / Copy URL / Stop
 *   - stopping → spinner, disabled
 *   - failed   → click opens a popover with the failure reason + Retry
 */
import React from 'react';
import { ArrowSquareOut, CircleNotch, Copy, Cube, StopCircle } from '@phosphor-icons/react';
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@chatsdet/ui';
import type { SandboxEnvironmentState } from '@chatsdet/types';
import { SandboxSetupPopover } from '../../../../features/sandboxes/components/SandboxSetupPopover';

export interface SandboxToolbarButtonProps {
  environmentName: string;
  sandbox: SandboxEnvironmentState;
  isEnsuring: boolean;
  isStopping: boolean;
  /** Warm (asleep) or retry (failed) the backing instance. */
  onStart: () => void;
  /** Collapse the TTL — teardown fires immediately. */
  onStop: () => void;
}

const ICON_COLOR: Record<SandboxEnvironmentState['status'], string> = {
  asleep: 'text-muted-foreground',
  warming: 'text-blue-500',
  ready: 'text-green-600',
  stopping: 'text-amber-500',
  failed: 'text-status-degraded',
};

export const SandboxToolbarButton: React.FC<SandboxToolbarButtonProps> = ({
  environmentName,
  sandbox,
  isEnsuring,
  isStopping,
  onStart,
  onStop,
}) => {
  const { status, url, failureReason, instanceStatus, createdAt } = sandbox;
  const color = ICON_COLOR[status];
  const transitional = status === 'warming' || status === 'stopping';

  const icon = transitional ? (
    <CircleNotch size={16} className={`${color} animate-spin`} />
  ) : (
    <Cube size={16} weight={status === 'ready' ? 'fill' : 'regular'} className={color} />
  );

  // Warming / failed: the button opens a popover with the provisioning
  // checklist + live timer (warming) or the failure reason + Retry (failed).
  if (status === 'warming' || status === 'failed') {
    return (
      <Popover placement="bottom">
        <PopoverTrigger>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip={
              status === 'warming'
                ? `Sandbox warming — ${environmentName}`
                : `Sandbox failed — ${environmentName}`
            }
            aria-label={`Sandbox — ${status}, view setup details`}
          >
            {icon}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <SandboxSetupPopover
            status={status}
            instanceStatus={instanceStatus}
            createdAt={createdAt}
            failureReason={failureReason}
            onStart={onStart}
            isEnsuring={isEnsuring}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Ready: the button opens a menu (Open / Copy URL / Stop).
  if (status === 'ready') {
    return (
      <MenuTrigger>
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip={`Sandbox ready — ${environmentName}`}
          aria-label="Sandbox — ready"
        >
          {icon}
        </Button>
        <Menu
          onAction={(key) => {
            if (key === 'open' && url) {
              window.open(url, '_blank', 'noopener,noreferrer');
            } else if (key === 'copy' && url) {
              void navigator.clipboard?.writeText(url).catch(() => undefined);
            } else if (key === 'stop') {
              onStop();
            }
          }}
        >
          {url ? (
            <MenuItem id="open">
              <ArrowSquareOut size={14} className="mr-2" />
              Open sandbox
            </MenuItem>
          ) : null}
          {url ? (
            <MenuItem id="copy">
              <Copy size={14} className="mr-2" />
              Copy URL
            </MenuItem>
          ) : null}
          <MenuItem id="stop" destructive isDisabled={isStopping}>
            <StopCircle size={14} className="mr-2" />
            {isStopping ? 'Stopping…' : 'Stop sandbox'}
          </MenuItem>
        </Menu>
      </MenuTrigger>
    );
  }

  // Stopping: disabled spinner with a status tooltip.
  if (status === 'stopping') {
    return (
      <Button
        variant="icon"
        size="sm"
        iconOnly
        isDisabled
        tooltip={`Sandbox stopping… — ${environmentName}`}
        aria-label="Sandbox — stopping"
      >
        {icon}
      </Button>
    );
  }

  // Asleep: a single press warms the backing instance.
  return (
    <Button
      variant="icon"
      size="sm"
      iconOnly
      isDisabled={isEnsuring}
      onPress={onStart}
      tooltip={`Warm up sandbox — ${environmentName}`}
      aria-label="Warm up sandbox"
    >
      {icon}
    </Button>
  );
};

SandboxToolbarButton.displayName = 'SandboxToolbarButton';
